import { CountryCodeSchema } from '../countries';
import { z } from 'zod/v3';
import { VendorRiskTierEnum, VendorDTOSchema } from './vendor.dto';
import { VendorAssessmentStatusSchema, VendorControlCoverageSchema, VendorRiskLevelSchema, RiskTreatmentSchema } from '../generated/enums';

export const VendorControlCoverageEnum = VendorControlCoverageSchema;

// ========== Vendor Assessment DTOs ==========

export const VendorAssessmentStatusEnum = VendorAssessmentStatusSchema;

// ── Assessment Section Schemas ──

// Versioned relationship facts; approval applies only these fields to the vendor.
export const VendorAssessmentContextSchema = VendorDTOSchema.pick({
  businessCriticality: true, inherentImpact: true, inherentLikelihood: true,
  customerDataInvolved: true, dataProcessingRole: true, containsEphi: true, baaStatus: true,
  vendorToCompanyAuthMethod: true, companyToVendorAuthMethod: true, environmentAccess: true,
}).partial().extend({
  baaSignedAt: z.preprocess(value => value instanceof Date ? value.toISOString() : value, z.string().datetime().nullable()).optional(),
  baaExpiresAt: z.preprocess(value => value instanceof Date ? value.toISOString() : value, z.string().datetime().nullable()).optional(),
});

export const VendorRiskResponseSchema = z.object({
  strategy: RiskTreatmentSchema.optional(),
  rationale: z.string().max(5000).optional(),
  riskIds: z.array(z.string().min(1)).max(50).default([]),
});
export type VendorRiskResponse = z.infer<typeof VendorRiskResponseSchema>;

export const SecurityPostureSchema = z.object({
  riskResponse: VendorRiskResponseSchema.optional(),
  assessedRisk: z.object({
    impact: VendorRiskLevelSchema.optional(),
    likelihood: VendorRiskLevelSchema.optional(),
  }).optional(),
  vendorContext: VendorAssessmentContextSchema.optional(),
  encryptionAtRest: z.boolean().nullable().optional(),
  encryptionInTransit: z.boolean().nullable().optional(),
  mfaEnforced: z.boolean().nullable().optional(),
  accessControlModel: z.string().nullable().optional(),
  vulnerabilityScanning: z.boolean().nullable().optional(),
  incidentResponsePlan: z.boolean().nullable().optional(),
  penTestFrequency: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  // TPRM extended fields (CLI/AI-populated)
  encryptionStandard: z.string().nullable().optional(),
  keyManagement: z.string().nullable().optional(),
  mfaMethod: z.string().nullable().optional(),
  ssoSupported: z.boolean().nullable().optional(),
  vulnerabilityScanningFrequency: z.string().nullable().optional(),
  lastPentestDate: z.string().nullable().optional(),
  bugBountyProgram: z.boolean().nullable().optional(),
  incidentNotificationSla: z.string().nullable().optional(),
  hostingProvider: z.string().nullable().optional(),
  datacenters: z.array(z.string()).nullable().optional(),
}).passthrough();

export const DataHandlingSchema = z.object({
  dataClassification: z.string().nullable().optional(),
  retentionPolicy: z.string().nullable().optional(),
  deletionProcess: z.string().nullable().optional(),
  crossBorderTransfer: z.boolean().nullable().optional(),
  dpaInPlace: z.boolean().nullable().optional(),
  subprocessors: z.array(z.string()).nullable().optional(),
  notes: z.string().nullable().optional(),
  // TPRM extended fields
  dataTypes: z.array(z.string()).nullable().optional(),
  processingPurpose: z.string().nullable().optional(),
  processingLocations: z.array(z.string()).nullable().optional(),
  processingCountryCodes: z.array(CountryCodeSchema).nullable().optional(),
  deletionVerification: z.boolean().nullable().optional(),
  transferMechanism: z.string().nullable().optional(),
  dpaUrl: z.string().nullable().optional(),
  ephiInvolved: z.boolean().nullable().optional(),
  baaAvailable: z.boolean().nullable().optional(),
  minimumNecessaryControls: z.string().nullable().optional(),
}).passthrough();

export const ComplianceCertsSchema = z.object({
  soc2: z.object({
    status: z.string().nullable().optional(),
    reportDate: z.string().nullable().optional(),
    type: z.string().nullable().optional(),
    // TPRM extended fields
    auditor: z.string().nullable().optional(),
    auditPeriod: z.string().nullable().optional(),
    opinion: z.string().nullable().optional(),
    exceptionsNoted: z.number().nullable().optional(),
    trustPageUrl: z.string().nullable().optional(),
  }).passthrough().nullable().optional(),
  iso27001: z.boolean().nullable().optional(),
  hipaa: z.boolean().nullable().optional(),
  gdpr: z.boolean().nullable().optional(),
  otherCerts: z.array(z.string()).nullable().optional(),
  auditReportsReviewed: z.boolean().nullable().optional(),
  notes: z.string().nullable().optional(),
  // TPRM extended fields
  iso27001Details: z.object({
    certified: z.boolean().nullable().optional(),
    certDate: z.string().nullable().optional(),
    registrar: z.string().nullable().optional(),
    scope: z.string().nullable().optional(),
    expiresAt: z.string().nullable().optional(),
  }).nullable().optional(),
  recentBreaches: z.array(z.object({
    date: z.string(),
    description: z.string(),
    recordsAffected: z.string().optional(),
    sourceUrl: z.string().optional(),
  })).nullable().optional(),
}).passthrough();

export const ContractualReviewSchema = z.object({
  hasCyberInsurance: z.boolean().nullable().optional(),
  slaTerms: z.string().nullable().optional(),
  liabilityCap: z.string().nullable().optional(),
  terminationClause: z.string().nullable().optional(),
  dataReturnOnTermination: z.boolean().nullable().optional(),
  insuranceCoverage: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  // TPRM extended fields
  dpaStatus: z.enum(['signed', 'available', 'not_available', 'not_required', 'unknown']).nullable().optional(),
  dpaUrl: z.string().nullable().optional(),
  dpaEffectiveDate: z.string().nullable().optional(),
  supportResponseTime: z.string().nullable().optional(),
  indemnification: z.string().nullable().optional(),
  cyberInsuranceMinimum: z.string().nullable().optional(),
  terminationNotice: z.string().nullable().optional(),
  terminationForConvenience: z.boolean().nullable().optional(),
  dataReturnPeriod: z.string().nullable().optional(),
  dataReturnFormat: z.string().nullable().optional(),
  breachNotificationPeriod: z.string().nullable().optional(),
  breachNotificationMethod: z.string().nullable().optional(),
  subprocessorListAvailable: z.boolean().nullable().optional(),
  subprocessorListUrl: z.string().nullable().optional(),
  subprocessorChangeNotification: z.boolean().nullable().optional(),
  auditRightsGranted: z.boolean().nullable().optional(),
  auditFrequency: z.string().nullable().optional(),
  auditScope: z.string().nullable().optional(),
  contractType: z.string().nullable().optional(),
  effectiveDate: z.string().nullable().optional(),
  expirationDate: z.string().nullable().optional(),
  autoRenewal: z.boolean().nullable().optional(),
  governingLaw: z.string().nullable().optional(),
  riskFlags: z.array(z.string()).nullable().optional(),
}).passthrough();

// ── RFC-231 §5: per-field provenance ──
export const FieldProvenanceSchema = z.object({
  source: z.enum(['document', 'research', 'analysis']),
  /** VendorAssessmentInput.id (DOCUMENT or TRUST_CENTER) when known, else the document id. Analysis entries use the assessment id. */
  inputId: z.string(),
  vaultDocumentId: z.string().optional(),
  page: z.number().nullable().optional(),
  url: z.string().optional(),
  /** Verbatim quote, ≤ 300 chars. */
  quote: z.string(),
  confidence: z.number().optional(),
  extractedAt: z.string(),
  /** Human clicked Accept — value is now protected from re-runs. */
  accepted: z.boolean().optional(),
});
export type FieldProvenance = z.infer<typeof FieldProvenanceSchema>;
/** Keyed by dotted field path, e.g. "securityPosture.mfaEnforced" or "complianceCerts.soc2.opinion". */
export type FieldProvenanceMap = Record<string, FieldProvenance>;
export const FieldProvenanceMapSchema = z.record(z.string(), FieldProvenanceSchema);

export const SetFieldDispositionDTOSchema = z.object({
  assessmentId: z.string(),
  path: z.string().min(1),
  action: z.enum(['accept', 'discard']),
});
export type SetFieldDispositionDTO = z.infer<typeof SetFieldDispositionDTOSchema>;

// ── Main DTOs ──

export const VendorAssessmentDTOSchema = z.object({
  scoringVersion: z.number().int().min(1).max(2).optional(),
  id: z.string(),
  vendorId: z.string(),
  vendorName: z.string(),
  vendorDisplayId: z.string(),
  workspaceId: z.string(),
  programId: z.string(),
  version: z.number(),
  status: VendorAssessmentStatusEnum,
  initiatedById: z.string(),
  initiatedByName: z.string().nullable(),
  submittedAt: z.coerce.date().nullable(),
  completedAt: z.coerce.date().nullable(),
  dueAt: z.coerce.date().nullable(),
  reviewerId: z.string().nullable(),
  reviewerName: z.string().nullable(),
  reviewedAt: z.coerce.date().nullable(),
  reviewComments: z.string().nullable(),
  securityPosture: SecurityPostureSchema.nullable(),
  dataHandling: DataHandlingSchema.nullable(),
  complianceCerts: ComplianceCertsSchema.nullable(),
  contractualReview: ContractualReviewSchema.nullable(),
  summary: z.string().nullable(),
  recommendedRiskTier: VendorRiskTierEnum.nullable(),
  fieldProvenance: FieldProvenanceMapSchema.nullable().optional(),
  evidencePackId: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type VendorAssessmentDTO = z.infer<typeof VendorAssessmentDTOSchema>;

export const CreateVendorAssessmentDTOSchema = z.object({
  vendorId: z.string(),
  reviewerId: z.string().optional(),
});
export type CreateVendorAssessmentDTO = z.infer<typeof CreateVendorAssessmentDTOSchema>;

export const UpdateVendorAssessmentDTOSchema = z.object({
  /** Editable on a draft: reviewer assignment and review due date. */
  reviewerId: z.string().nullable().optional(),
  dueAt: z.coerce.date().nullable().optional(),
  securityPosture: SecurityPostureSchema.nullable().optional(),
  dataHandling: DataHandlingSchema.nullable().optional(),
  complianceCerts: ComplianceCertsSchema.nullable().optional(),
  contractualReview: ContractualReviewSchema.nullable().optional(),
  summary: z.string().nullable().optional(),
  recommendedRiskTier: VendorRiskTierEnum.nullable().optional(),
});
export type UpdateVendorAssessmentDTO = z.infer<typeof UpdateVendorAssessmentDTOSchema>;

export const SubmitVendorAssessmentDTOSchema = z.object({
  reviewerId: z.string(),
  summary: z.string().min(1),
  recommendedRiskTier: VendorRiskTierEnum,
});
export type SubmitVendorAssessmentDTO = z.infer<typeof SubmitVendorAssessmentDTOSchema>;

export const ReviewVendorAssessmentDTOSchema = z.object({
  status: z.enum(['APPROVED', 'REJECTED', 'NEEDS_REVISION']),
  comments: z.string().optional(),
});
export type ReviewVendorAssessmentDTO = z.infer<typeof ReviewVendorAssessmentDTOSchema>;

// ── Vendor Control Mappings (#370/#469) ──

/** A persisted vendor → our-control coverage mapping. */
export const VendorControlMappingDTOSchema = z.object({
  displayCode: z.string().nullable().optional(),
  id: z.string(),
  assessmentId: z.string(),
  vendorId: z.string(),
  controlId: z.string(),
  controlTitle: z.string(),
  coverage: VendorControlCoverageEnum,
  rationale: z.string(),
  documentId: z.string().nullable(),
  evidenceCitations: z.unknown().nullable(),
  locked: z.boolean(),
  lockedAt: z.coerce.date().nullable(),
  lockedById: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type VendorControlMappingDTO = z.infer<typeof VendorControlMappingDTOSchema>;

/** One coverage suggestion to persist (from document extraction or manual). */
export const VendorControlMappingInputSchema = z.object({
  controlId: z.string().min(1),
  controlTitle: z.string(),
  coverage: VendorControlCoverageEnum,
  rationale: z.string(),
  documentId: z.string().nullable().optional(),
  evidenceCitations: z.unknown().nullable().optional(),
});
export type VendorControlMappingInput = z.infer<typeof VendorControlMappingInputSchema>;

export const VendorEvidenceRequestStatusEnum = z.enum(['OUTSTANDING', 'RECEIVED', 'WAIVED']);

export const VendorEvidenceRequestDTOSchema = z.object({
  id: z.string(),
  assessmentId: z.string(),
  vendorId: z.string(),
  workspaceId: z.string(),
  programId: z.string(),
  docType: z.string(),
  label: z.string(),
  required: z.boolean(),
  status: VendorEvidenceRequestStatusEnum,
  receivedDocId: z.string().nullable(),
  receivedAt: z.coerce.date().nullable(),
  waivedReason: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type VendorEvidenceRequestDTO = z.infer<typeof VendorEvidenceRequestDTOSchema>;
